import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/Button";
import {
  Bold,
  HeaderTextBlock,
  P,
  TypographyContainer,
} from "@/components/Typography";
import Card from "@/components/Card/Card";
import { faSave } from "@fortawesome/pro-solid-svg-icons";
import { usePostMatIntegrationConfiguration } from "@/requests/hooks/mat";
import { useParams } from "react-router-dom";
import { useEffect } from "react";
import { toast } from "react-toastify";
import stringToTitleCase from "@/utils/stringToTitleCase";
import { useQueryClient } from "@tanstack/react-query";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { Alert } from "@/components/Alert";
import { faExclamationTriangle } from "@fortawesome/pro-duotone-svg-icons";
import { useTranslation } from "react-i18next";

interface ISaveConfigurationError {
  identifier: string;
  message: string;
}

interface IProps extends IMatIntegrationConfigurationItem {
  channel: IContactChannel | null;
  renderNonVideos: () => JSX.Element[] | undefined;
  renderVideos: () => JSX.Element[] | undefined;
}

const MatCard = (props: IProps) => {
  const {
    isForm,
    title,
    renderNonVideos,
    renderVideos,
    validationDescription,
    validationState,
    channel,
    properties,
  } = props;

  const { integration } = useParams();
  const { t } = useTranslation();
  const methods = useForm();
  const queryClient = useQueryClient();
  const {
    onboardingStatus: { isOnboarding },
  } = useOnboardingStatus();
  const {
    mutate: saveMatIntegrationConfiguration,
    isPending: isMatIntegrationConfigurationPending,
    isSuccess: isMatIntegrationConfigurationSuccess,
    isError: isMatIntegrationConfigurationError,
    data: saveMatIntegrationConfigurationData,
  } = usePostMatIntegrationConfiguration(
    integration!,
    isOnboarding,
    queryClient,
  );

  useEffect(() => {
    /**
     * Check for our standard error response in the data returned, if it exists
     * then we'll show a toast (and Alert further on), and bail out early. We
     * bail out early here since `isMatIntegrationConfigurationSuccess` is
     * technically true in this scenario (the request succeeded).
     */
    if (!!saveMatIntegrationConfigurationData?.errors) {
      toast.error(
        `Failed to update ${stringToTitleCase(integration)} integration`,
      );
      return;
    }

    if (isMatIntegrationConfigurationSuccess) {
      toast.success(`Updated ${stringToTitleCase(integration)} integration`);
    }

    if (isMatIntegrationConfigurationError) {
      toast.error(
        `Failed to update ${stringToTitleCase(integration)} integration`,
      );
    }
  }, [
    isMatIntegrationConfigurationSuccess,
    isMatIntegrationConfigurationError,
  ]);

  const renderErrors = () => {
    if (!saveMatIntegrationConfigurationData?.errors) return null;

    const errorMap = saveMatIntegrationConfigurationData?.errors?.map(
      ({ identifier, message }: ISaveConfigurationError) => {
        /**
         * We want to find the human readable name for the identifier
         * that has been returned, this is available in the data for this
         * MatCard, so we'll find it in there, and then display it.
         *
         * Filter the properties being displayed in this card to only be
         * comprised of form elements (they have an identifier). Attempt
         * to match the error from the data to the properties and pull the
         * label. Fallback to the raw identifier if necessary.
         */
        const humanReadableLabel =
          properties
            .filter(
              property =>
                property?.type === "INPUT" || property?.type === "SELECT",
            )
            .find(property => property?.identifier === identifier)?.label ??
          identifier;

        return (
          <li key={identifier}>
            {humanReadableLabel}: {message}
          </li>
        );
      },
    );

    return (
      <div className="mb-40">
        <Alert
          icon={faExclamationTriangle}
          variant="error"
          titleSlot={<Bold>{t("components.matCard.errors")}</Bold>}>
          <ul className="list-inside list-disc">{errorMap}</ul>
        </Alert>
      </div>
    );
  };

  /**
   * Normally in a react-hook-form onSubmit, the data would be a strong typed object,
   * as we know what form elements are in the form. However, since the MAT Config form
   * is dynamically generated, we need to accept `any` as the type here.
   */
  const onSubmit = (data: any) => {
    /**
     * Right now, we have two dynamic form elements, an input and a select. The input will
     * alwats return a `key: value` format. However, the select will return a key: object
     * format, with the value inside said object. The below code is designed to check if
     * the value is an object, and automatically pull that out into the same format as the
     * input (key: value).
     */
    const formData: any = {};

    /**
     * If the channel is available, add it to the formData, also, if we have a channel
     * available, we need to put any further properties into a `channelIntegrationParameters` sub-object.
     */
    if (!!channel) {
      formData["channel"] = channel;
      formData["channelIntegrationParameters"] = {};
    }

    /**
     * Magic the dynamic values into a structure that the BE will accept and be able to
     * handle for us.
     */
    Object.keys(data).forEach(key => {
      const value = !!data[key]?.value ? data[key].value : data[key];
      if (!!channel) {
        formData["channelIntegrationParameters"][key] = value;
      } else {
        formData[key] = value;
      }
    });

    /**
     * Submit the form
     */
    saveMatIntegrationConfiguration({
      integration: integration!,
      body: formData,
    });
  };

  const renderFormBody = () => {
    return (
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-40">
          {!validationState && validationDescription && (
            <P className="text-red">{validationDescription}</P>
          )}
          {renderNonVideos()}
          <div className="flex flex-row justify-start">
            <Button
              type="submit"
              width="lg"
              icon={faSave}
              iconPosition="start"
              iconClassName="mr-5"
              loading={isMatIntegrationConfigurationPending}>
              Save
            </Button>
          </div>
        </form>
      </FormProvider>
    );
  };

  return (
    <Card
      additionalClassName="px-20 pt-20 pb-40 border-gray-light shadow-lg"
      shadow={true}
      border>
      {renderErrors()}
      <TypographyContainer>
        <HeaderTextBlock featureText={title!} />
      </TypographyContainer>
      <div className="flex flex-col gap-30 sm:flex-row">
        <div className="flex w-full flex-col gap-40 sm:w-7/12">
          {isForm ? renderFormBody() : renderNonVideos()}
        </div>
        <div className="flex w-full flex-row justify-center gap-40 sm:w-5/12 sm:flex-col sm:justify-start">
          {renderVideos()}
        </div>
      </div>
    </Card>
  );
};

export default MatCard;
